import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  CircularProgress
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { PasswordTextField } from '../../elements';
import PasswordPolicyChecklist from '../PasswordPolicyChecklist';
import {
  notifications,
  extractApiErrorMessage,
  extractApiErrorCode,
  validatePasswordPolicy,
  AuthErrorCode
} from '../../utils';
import { ApiService } from '../../hooks';
import useFeatures, { FeatureType } from '../../hooks/features';
import { useStyles } from './styles';

interface IProps {
  opened: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<IProps> = ({ opened, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const apiService = new ApiService();
  const { hasFeature } = useFeatures();
  const passwordPolicyEnabled = hasFeature(FeatureType.USE_PASSWORD_POLICY);

  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [currentPasswordError, setCurrentPasswordError] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  React.useEffect(() => {
    if (opened) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setCurrentPasswordError(false);
      setConfirmError(false);
    }
  }, [opened]);

  const policy = validatePasswordPolicy(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit =
    currentPassword.length > 0 && (!passwordPolicyEnabled || policy.isValid) && passwordsMatch && !isBusy;

  const handleSubmit = () => {
    setCurrentPasswordError(false);
    setConfirmError(false);

    if (!currentPassword) {
      setCurrentPasswordError(true);
      notifications.warning(t('f-change-password-current-required'));
      return;
    }

    if (passwordPolicyEnabled && !policy.isValid) {
      notifications.warning(t('f-change-password-policy-warning'));
      return;
    }

    if (!passwordsMatch) {
      setConfirmError(true);
      notifications.warning(t('f-change-password-mismatch'));
      return;
    }

    setIsBusy(true);

    apiService.api.userAccounts
      .userAccountsChangePassword({ currentPassword, newPassword }, { format: 'json' })
      .then(() => {
        notifications.success(t('f-change-password-success'));
        onClose();
      })
      .catch((e: unknown) => {
        const code = extractApiErrorCode(e);
        if (code === AuthErrorCode.CurrentPasswordIncorrect) {
          setCurrentPasswordError(true);
          notifications.error(t('f-change-password-current-incorrect'));
        } else {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-change-password-error') }));
        }
      })
      .finally(() => setIsBusy(false));
  };

  return (
    <Dialog
      id="changePasswordDialog"
      open={opened}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="xs"
      fullWidth
      aria-labelledby="change-password-dialog-title"
    >
      <DialogTitle id="changePasswordDialogTitle" className={classes.dialogHeaderFooter}>
        {t('f-change-password-title')}
      </DialogTitle>

      <DialogContent>
        <Grid item container direction="column">
          <Grid item>
            <PasswordTextField
              variant="outlined"
              size="small"
              margin="dense"
              fullWidth
              sx={{ m: 1 }}
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                if (currentPasswordError) setCurrentPasswordError(false);
              }}
              id="changePasswordCurrent"
              label={t('f-change-password-current')}
              error={currentPasswordError}
              disabled={isBusy}
            />
          </Grid>

          <Grid item>
            <PasswordTextField
              variant="outlined"
              size="small"
              margin="dense"
              fullWidth
              sx={{ m: 1 }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              id="changePasswordNew"
              label={t('f-change-password-new')}
              disabled={isBusy}
            />
          </Grid>

          {passwordPolicyEnabled && (
            <Grid item>
              <PasswordPolicyChecklist password={newPassword} />
            </Grid>
          )}

          <Grid item>
            <PasswordTextField
              variant="outlined"
              size="small"
              margin="dense"
              fullWidth
              sx={{ m: 1 }}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (confirmError) setConfirmError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              id="changePasswordConfirm"
              label={t('f-change-password-confirm')}
              error={confirmError}
              disabled={isBusy}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        {isBusy && <CircularProgress size={24} />}

        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isBusy}
          startIcon={<Icon>{'close'}</Icon>}
          color="primary"
        >
          {t('f-com-cancel')}
        </Button>

        <Button
          id="changePasswordSubmitBtn"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={<Icon>{'lock'}</Icon>}
          variant="contained"
          color="primary"
        >
          {t('f-change-password-submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;
